import { Link } from "react-router-dom";
import { Home as HomeIcon, GraduationCap, Heart, Sunset, Plane, ArrowRight, TrendingDown, HandCoins, ShieldAlert } from "lucide-react";

const GOALS = [
  {
    icon: <HomeIcon size={20} />,
    name: "Owning a Home",
    build: "A structured investment plan for your down payment, timed so your money shifts to safer assets as the purchase date approaches.",
    protect: "Cover on the earning member's life sized to clear the outstanding loan, so a home loan never becomes a debt burden passed on to your family.",
  },
  {
    icon: <GraduationCap size={20} />,
    name: "Child's Education",
    build: "A long-horizon investment plan started as early as possible — because education costs in India have been rising sharply faster than general inflation for over a decade.",
    protect: "A plan structured so the education goal is funded on schedule regardless of what happens to the parent who was contributing to it — so the goal doesn't disappear along with the income.",
    inflationLink: true,
  },
  {
    icon: <Heart size={20} />,
    name: "Child's Marriage",
    build: "Another major, largely predictable goal that benefits enormously from starting early and letting compounding do the heavy lifting.",
    protect: "The same principle — this goal, once funded and protected, isn't derailed by an unplanned emergency elsewhere in the family's finances.",
  },
  {
    icon: <Sunset size={20} />,
    name: "Retirement",
    build: "Decades of consistent, disciplined investing, accounting for inflation and a retirement that could last 20–30 years.",
    protect: "Adequate health cover reviewed periodically — because rising healthcare costs in later life are one of the single biggest threats to a retirement corpus, and a policy that felt sufficient years ago may no longer be.",
  },
  {
    icon: <Plane size={20} />,
    name: "Travel & Life Goals",
    build: "A dedicated plan for the life you want to live, so it doesn't have to compete with your other financial priorities.",
    protect: "A stable emergency fund and adequate cover elsewhere in your plan, so a life goal like this is never the thing you're forced to sacrifice first when something unexpected happens.",
  },
];

const EXIGENCIES = [
  {
    icon: <TrendingDown size={22} />,
    title: "Death of the bread earner",
    body: "Replacing lost income so the family's goals continue uninterrupted.",
  },
  {
    icon: <ShieldAlert size={22} />,
    title: "Permanent disability",
    body: "Covering lost income and the added cost of care when someone can no longer work.",
  },
  {
    icon: <HandCoins size={22} />,
    title: "Loss of job or business",
    body: "An emergency fund and income-protection structure that absorbs the shock without forcing you to break long-term investments at the worst possible time.",
  },
];

export default function YourGoals() {
  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-14">
          <div className="eyebrow mb-6">Your Goals</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink max-w-4xl">
            Every goal has a<br />
            <span className="italic text-brass-deep">Build</span> line and a{" "}
            <span className="italic text-brass-deep">Protect</span> line.
          </h1>
          <p className="mt-8 text-[16px] md:text-[17px] text-ink-soft leading-relaxed max-w-3xl">
            Every strategy starts with your goals — and for each one, we build both the growth plan
            to get there and the protection that keeps it safe along the way.
          </p>
        </div>
      </section>

      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="space-y-6">
            {GOALS.map((g, i) => (
              <div key={g.name} className="card-paper p-6 md:p-8" data-testid={`goal-card-${i}`}>
                <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start">
                  <div className="md:col-span-4">
                    <div className="flex items-center gap-3 text-brass">
                      {g.icon}
                      <span className="num text-[12px] tracking-widest">GOAL 0{i + 1}</span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl mt-3 text-ink leading-tight">
                      {g.name}
                    </h3>
                  </div>
                  <div className="md:col-span-4 ledger-rule">
                    <div className="eyebrow mb-2">Build</div>
                    <p className="text-[14.5px] text-ink leading-relaxed">
                      {g.build}
                      {g.inflationLink && (
                        <>
                          {" "}
                          <Link to="/why-inflation-matters" className="link-brass">See the numbers →</Link>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="md:col-span-4 ledger-rule">
                    <div className="eyebrow mb-2">Protect</div>
                    <p className="text-[14.5px] text-ink leading-relaxed">{g.protect}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="eyebrow mb-4">The exigencies every plan accounts for</div>
          <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight max-w-3xl">
            Regardless of which goals apply to you,<br />
            every strategy is protected against the same three unplanned events.
          </h2>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {EXIGENCIES.map((e) => (
              <div key={e.title} className="card-paper p-6">
                <div className="text-brass mb-3">{e.icon}</div>
                <div className="font-display text-[20px] text-ink">{e.title}</div>
                <p className="text-[14px] text-ink-soft leading-relaxed mt-3">{e.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <a
              href="https://reliservepulse.com/book-consultation"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-brass"
              data-testid="goals-cta"
            >
              Build Your Goal-and-Protection Plan <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
